import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { obterPerfilPublico, baixarCurriculoCandidato, vagasCompativeisCom } from "../services/api";

// Modal do candidato — usado na busca de talentos E na lista de compatíveis de uma vaga.
// Era um accordion dentro do card na busca e um modal na vaga: a mesma informação com duas caras, e o
// recrutador aprendia duas vezes. Aqui é uma tela só.
//
// O contato (e-mail/telefone) NÃO aparece de propósito: a conversa passa pelo eaiRH, e é isso que permite
// cobrar o retorno depois — contato solto na tela leva a conversa para fora e o candidato volta ao vácuo.
//
// `vaga` é opcional: quando vem, as skills que a vaga pede ganham ✓ verde. `rodape` é opcional e recebe as
// ações do contexto (dar retorno na vaga, "tenho interesse" na busca).
export default function ModalCandidato({ usuarioId, nome, headline, match, motivos, jaAbordado, vaga, rodape, onFechar }) {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [erro, setErro] = useState(null);
  const [baixando, setBaixando] = useState(false);
  // "Em quais das MINHAS vagas esta pessoa encaixa?" — a pergunta que a empresa faz ao esbarrar num bom perfil
  // fora do contexto de uma vaga. Sem isto, ela abriria vaga por vaga procurando o mesmo nome.
  const [vagasComp, setVagasComp] = useState(null);

  useEffect(() => { obterPerfilPublico(usuarioId).then(setP).catch((e) => setErro(e.message)); }, [usuarioId]);
  useEffect(() => { vagasCompativeisCom(usuarioId).then(setVagasComp).catch(() => setVagasComp([])); }, [usuarioId]);

  // Fecha no ESC — modal que só fecha no × prende quem usa teclado.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onFechar(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onFechar]);

  const abrirCv = async () => {
    setErro(null); setBaixando(true);
    try { await baixarCurriculoCandidato(usuarioId); }
    catch (e) { setErro(e.message); }
    finally { setBaixando(false); }
  };

  const dinheiro = (v) => v == null ? null : `R$ ${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
  const corMatch = (m) => m >= 70 ? "bg-green-100 text-green-800" : m >= 40 ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-600";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={onFechar}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-xl font-bold text-gray-900">{nome || p?.nome}</h3>
              {match != null && <span className={`font-bold rounded-full px-2.5 py-1 text-xs ${corMatch(match)}`}>{match}% de match</span>}
              {jaAbordado && <span className="text-[11px] rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 font-semibold">já abordado nesta vaga</span>}
            </div>
            {(headline || p?.headline) && <p className="text-sm text-gray-600 mt-0.5">{headline || p?.headline}</p>}
            {motivos?.length > 0 && <p className="text-xs text-gray-500 mt-1">Por que deu match: {motivos.join(" · ")}</p>}
          </div>
          <button onClick={onFechar} aria-label="Fechar" className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-5">
          {erro && <p role="alert" className="text-red-600 text-sm">{erro}</p>}
          {!p && !erro && <p className="text-gray-500 text-sm">Carregando perfil...</p>}

          {p && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
                <Dado rotulo="Senioridade">{p.senioridade}</Dado>
                <Dado rotulo="Área">{p.areaAtuacao}</Dado>
                <Dado rotulo="Cidade">{p.cidade}</Dado>
                <Dado rotulo="Disponibilidade">{p.disponibilidadeTexto}</Dado>
                <Dado rotulo="Aceita">{[...(p.modalidadesAceitas || []), ...(p.contratacoesAceitas || [])].join(", ") || null}</Dado>
                <Dado rotulo="Pretensão CLT">{dinheiro(p.pretensaoClt)}</Dado>
                <Dado rotulo="Pretensão PJ">{dinheiro(p.pretensaoPj)}</Dado>
                <Dado rotulo="Currículo">{p.temCurriculo ? "anexado" : null}</Dado>
              </div>

              {p.resumo && (
                <div>
                  <Rotulo>Resumo</Rotulo>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{p.resumo}</p>
                </div>
              )}

              {p.skills?.length > 0 && (
                <div>
                  <Rotulo>Skills ({p.skills.length})</Rotulo>
                  <div className="flex flex-wrap gap-1.5">
                    {p.skills.map((s) => {
                      const pedida = (vaga?.skills || []).some((x) => x.toLowerCase() === s.toLowerCase());
                      return (
                        <span key={s} title={pedida ? "Esta skill é pedida na vaga" : undefined}
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${pedida ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"}`}>
                          {pedida && "✓ "}{s}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {p.experiencias?.length > 0 && (
                <div>
                  <Rotulo>Experiência</Rotulo>
                  <ul className="space-y-2">
                    {p.experiencias.map((e, i) => (
                      <li key={i} className="text-sm">
                        <span className="font-semibold text-gray-900">{e.cargo}</span>
                        {e.empresa && <span className="text-gray-600"> · {e.empresa}</span>}
                        {e.periodo && <span className="text-gray-400 text-xs"> ({e.periodo})</span>}
                        {e.descricao && <p className="text-gray-600 text-xs mt-0.5 whitespace-pre-line">{e.descricao}</p>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {p.formacoes?.length > 0 && (
                <div>
                  <Rotulo>Formação</Rotulo>
                  <ul className="space-y-1">
                    {p.formacoes.map((f, i) => (
                      <li key={i} className="text-sm text-gray-700">
                        {f.curso}{f.instituicao && <span className="text-gray-600"> · {f.instituicao}</span>}
                        {f.periodo && <span className="text-gray-400 text-xs"> ({f.periodo})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {p.certificados?.length > 0 && (
                <div>
                  <Rotulo>Certificações</Rotulo>
                  <ul className="space-y-1">
                    {p.certificados.map((c, i) => (
                      <li key={i} className="text-sm text-gray-700">
                        {c.nome}{c.emissor && <span className="text-gray-600"> · {c.emissor}</span>}
                        {c.ano && <span className="text-gray-400 text-xs"> ({c.ano})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={abrirCv} disabled={!p.temCurriculo || baixando}
                  className="rounded-xl bg-gray-900 hover:bg-black disabled:opacity-40 text-white text-sm font-semibold px-4 py-2">
                  📄 {baixando ? "Abrindo..." : p.temCurriculo ? "Abrir currículo" : "Sem currículo anexado"}
                </button>
                {p.linkedinUrl && (
                  <a href={p.linkedinUrl} target="_blank" rel="noopener noreferrer"
                    className="rounded-xl border border-gray-300 hover:bg-gray-50 text-sm font-semibold px-4 py-2">LinkedIn</a>
                )}
              </div>
            </>
          )}

          {vagasComp?.length > 0 && (
            <div className="pt-2 border-t border-gray-200">
              <Rotulo>Compatível com {vagasComp.length} das suas vagas</Rotulo>
              <ul className="space-y-1.5">
                {vagasComp.slice(0, 8).map((v) => (
                  <li key={v.vagaId}>
                    <button onClick={() => { onFechar(); navigate(`/vagas/${v.vagaId}`); }}
                      className="w-full text-left rounded-lg border border-gray-200 hover:border-primary hover:bg-gray-50 px-3 py-2 transition">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-bold rounded-full px-2 py-0.5 text-[11px] ${corMatch(v.match)}`}>{v.match}%</span>
                        <span className="text-sm font-semibold text-gray-900">{v.titulo}</span>
                        {v.jaCandidatou && <span className="text-[10px] rounded-full bg-green-50 text-green-700 px-1.5 py-0.5 font-semibold">já se candidatou</span>}
                        {v.jaAbordado && !v.jaCandidatou && <span className="text-[10px] rounded-full bg-blue-50 text-blue-700 px-1.5 py-0.5 font-semibold">já abordado</span>}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        {[v.senioridade, v.cidade || (v.remoto ? "Remoto" : null)].filter(Boolean).join(" · ")}
                        {v.motivos?.length > 0 && ` · ${v.motivos.join(" · ")}`}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {rodape && <div className="pt-2 border-t border-gray-200">{rodape}</div>}
        </div>
      </div>
    </div>
  );
}

function Rotulo({ children }) {
  return <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">{children}</div>;
}

// Campo vazio aparece como "não informado" em cinza, e não some: some é diferente de não informado.
function Dado({ rotulo, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{rotulo}</div>
      <div className={`mt-0.5 ${children ? "text-gray-900" : "text-gray-400 italic"}`}>{children || "não informado"}</div>
    </div>
  );
}
